import React, { useState, useEffect } from "react";
import api from "../services/api";
import { getComprasByProducto, getVentasByProducto } from "../services/historial";
import { HistorialOperacion } from "../types";
import "../styles/ProductHistory.css"; // Reuse existing styles or adapt

interface HistorialMovimiento {
  id: number;
  tipo_movimiento: 'ENTRADA' | 'SALIDA' | 'AJUSTE' | 'DEVOLUCION';
  cantidad_anterior: number;
  cantidad_nueva: number;
  motivo: string;
  createdAt: string;
  User?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface ProductHistoryProps {
  productId: number;
}

const ProductHistory: React.FC<ProductHistoryProps> = ({ productId }) => {
  const [movimientos, setMovimientos] = useState<HistorialMovimiento[]>([]);
  const [compras, setCompras] = useState<HistorialOperacion[]>([]);
  const [ventas, setVentas] = useState<HistorialOperacion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [kardexRes, comprasRes, ventasRes] = await Promise.all([
          api.get(`/kardex/${productId}`).then(res => res.data).catch(() => []),
          getComprasByProducto(productId).catch(() => []),
          getVentasByProducto(productId).catch(() => [])
        ]);

        setMovimientos(kardexRes);
        setCompras(comprasRes);
        setVentas(ventasRes);
      } catch (err: any) {
        setError("Error al cargar el historial del producto.");
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchData();
    }
  }, [productId]);

  if (loading) return <div>Cargando Historial...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="history-container">
      <div className="history-table">
        <h2>Kardex (Historial de Movimientos)</h2>
        {movimientos.length === 0 ? (
          <p>No hay movimientos registrados para este producto.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Cant. Anterior</th>
                <th>Diferencia</th>
                <th>Cant. Nueva</th>
                <th>Motivo</th>
                <th>Usuario</th>
              </tr>
            </thead>
            <tbody>
              {movimientos.map((mov) => {
                const diferencia = mov.cantidad_nueva - mov.cantidad_anterior;
                const diffColor = diferencia > 0 ? 'green' : (diferencia < 0 ? 'red' : 'gray');
                const diffDisplay = diferencia > 0 ? `+${diferencia}` : `${diferencia}`;

                return (
                  <tr key={`kardex-${mov.id}`}>
                    <td>{new Date(mov.createdAt).toLocaleString()}</td>
                    <td>
                      <span className={`tipo-badge ${mov.tipo_movimiento.toLowerCase()}`}>
                        {mov.tipo_movimiento}
                      </span>
                    </td>
                    <td>{mov.cantidad_anterior}</td>
                    <td style={{ color: diffColor, fontWeight: 'bold' }}>{diffDisplay}</td>
                    <td>{mov.cantidad_nueva}</td>
                    <td>{mov.motivo}</td>
                    <td>{mov.User ? `${mov.User.firstName} ${mov.User.lastName}` : "Sistema"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {(compras.length > 0 || ventas.length > 0) && (
        <React.Fragment>
          <hr style={{ margin: '2rem 0', borderColor: '#eee' }} />
          <h3>Registros Legacy</h3>
        </React.Fragment>
      )}

      {compras.length > 0 && (
        <div className="history-table">
          <h2>Historial de Compras (Legacy)</h2>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Proveedor</th>
                <th>Cantidad</th>
                <th>Precio Unitario</th>
                <th>Total</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {compras.map((compra) => (
                <tr key={`compra-${compra.id}`}>
                  <td>{compra.id}</td>
                  <td>{compra.Proveedor?.nombre || "N/A"}</td>
                  <td>{compra.cantidad}</td>
                  <td>${compra.precioUnitario}</td>
                  <td>${compra.total}</td>
                  <td>{new Date(compra.fecha).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ventas.length > 0 && (
        <div className="history-table">
          <h2>Historial de Ventas (Legacy)</h2>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Cantidad</th>
                <th>Precio Unitario</th>
                <th>Total</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {ventas.map((venta) => (
                <tr key={`venta-${venta.id}`}>
                  <td>{venta.id}</td>
                  <td>{venta.cantidad}</td>
                  <td>${venta.precioUnitario}</td>
                  <td>${venta.total}</td>
                  <td>{new Date(venta.fecha).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProductHistory;
